import { ControllerProps, FieldPath, FieldValues } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState, useMemo } from "react";

import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";

export type ComboboxMultitpleOption<T = unknown> = {
  value: string;
  label: string;
  raw?: T;
};

export type FormMultipleComboboxStaticProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, "render"> & {
  options: ComboboxMultitpleOption[];
  label?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  description?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  inputClassName?: string;
  messageClassName?: string;
  disabled?: boolean;
  onSelectItem?: (option: any) => void;
  onReload?: () => void;
  onLoading?: boolean;
  preserveMessageSpace?: boolean;
  editable?: boolean; // Support both editable and readonly badge variants
};

const maxBadge = 3;

export const FormMultipleComboboxStatic = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  options,
  control,
  label,
  name,
  placeholder = "Select Option",
  searchPlaceholder = "Search...",
  wrapperClassName,
  labelClassName,
  triggerClassName,
  contentClassName,
  messageClassName,
  disabled,
  onSelectItem,
  preserveMessageSpace = false,
  required,
  editable = true, // Default to editable variant with close buttons
}: FormMultipleComboboxStaticProps<TFieldValues, TName>) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const allOptionsSelected = field.value?.length === options.length;
        const selectedOptions = options.filter((option) =>
          field.value?.map((val: any) => val.toString()).includes(option.value)
        );
        const displayedBadges = selectedOptions.slice(0, maxBadge);
        const remainingCount = selectedOptions.length - maxBadge;

        return (
          <FormItem
            className={cn("aero:w-full", wrapperClassName)}
            aria-invalid={!!fieldState?.error}
          >
            {label && (
              <FormLabel
                className={cn(
                  required &&
                    "aero:flex aero:items-center aero:justify-between",
                  labelClassName
                )}
              >
                {label}
                {required && (
                  <span className="aero:text-destructive" aria-hidden="true">
                    *
                  </span>
                )}
              </FormLabel>
            )}
            <FormControl>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild disabled={disabled}>
                  <div
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                      "aero:flex aero:w-full aero:items-center aero:justify-between aero:gap-2",
                      "aero:min-h-10 aero:px-4 aero:py-3",
                      "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
                      "aero:shadow-none",
                      "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                      "aero:cursor-pointer",
                      "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
                      fieldState?.error &&
                        "aero:border-destructive aero:ring-destructive/20",
                      disabled && "aero:cursor-not-allowed aero:opacity-50",
                      triggerClassName
                    )}
                  >
                    <div
                      className={cn(
                        "aero:flex aero:flex-1 aero:min-w-0 aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
                        field.value?.length > 0
                          ? "aero:text-foreground"
                          : "aero:text-neutral-500"
                      )}
                    >
                      {field.value?.length > 0 ? (
                        <div className="aero:flex aero:items-center aero:gap-2 aero:flex-wrap">
                          {displayedBadges.map((option) => (
                            <Badge
                              key={option.value}
                              variant={editable ? "editable" : "readonly"}
                              className="aero:flex aero:items-center aero:gap-2"
                            >
                              <span>{option.label}</span>
                              {editable && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newValue = field.value.filter(
                                      (v: any) => String(v) !== option.value
                                    );
                                    field.onChange(newValue);
                                    if (onSelectItem) {
                                      onSelectItem(newValue);
                                    }
                                  }}
                                  className="aero:h-3.5 aero:w-3.5 aero:flex aero:items-center aero:justify-center aero:rounded-full aero:hover:bg-brand-primary-100 aero:transition-colors"
                                >
                                  <X className="aero:h-3 aero:w-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                          {remainingCount > 0 && (
                            <Badge variant={editable ? "editable" : "readonly"}>
                              +{remainingCount} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        placeholder
                      )}
                    </div>
                    <ChevronsUpDown className="aero:h-4 aero:w-4 aero:shrink-0 aero:text-neutral-500" />
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className={cn(
                    "aero:w-(--radix-popover-trigger-width) aero:p-0",
                    contentClassName
                  )}
                  align="start"
                >
                  <div className="aero:flex aero:items-center aero:border-b aero:px-3">
                    <Search className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
                    <Input
                      placeholder={searchPlaceholder}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="aero:h-9 aero:border-0 aero:shadow-none aero:focus-visible:ring-0"
                    />
                    {search && (
                      <span
                        role="button"
                        className="aero:p-1 aero:rounded-full aero:hover:bg-muted aero:cursor-pointer"
                        onClick={() => setSearch("")}
                      >
                        <X className="aero:h-3 aero:w-3 aero:opacity-50" />
                      </span>
                    )}
                  </div>
                  <ScrollArea className="aero:h-60">
                    {filteredOptions.length === 0 ? (
                      <div className="aero:m-2 aero:flex aero:flex-col aero:items-center aero:justify-center aero:gap-2 aero:rounded aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:py-2 aero:px-2 aero:h-56">
                        <span className="aero:font-inter aero:font-normal aero:text-sm aero:leading-5 aero:text-center aero:text-foreground">
                          No results found
                        </span>
                      </div>
                    ) : (
                      <div className="aero:p-1">
                        {/* Select All / Clear Selection */}
                        <div
                          role="option"
                          className={cn(
                            "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none aero:font-medium",
                            "aero:hover:bg-neutral-100 aero:hover:text-foreground"
                          )}
                          onClick={() => {
                            const allOptionValues = options.map((i) => i.value);
                            const isAllSelected = allOptionValues.every((opt) =>
                              field.value
                                ?.map((f: any) => String(f))
                                .includes(opt)
                            );

                            if (isAllSelected) {
                              field.onChange([]);
                              if (onSelectItem) {
                                onSelectItem([]);
                              }
                            } else {
                              field.onChange(allOptionValues);
                              if (onSelectItem) {
                                onSelectItem(allOptionValues);
                              }
                            }
                          }}
                        >
                          <span className="aero:truncate">
                            {allOptionsSelected
                              ? "Clear Selection"
                              : "Select all"}
                          </span>
                          {allOptionsSelected && (
                            <Check className="aero:ml-auto aero:h-4 aero:w-4" />
                          )}
                        </div>

                        {/* Options */}
                        {filteredOptions.map((option) => {
                          const isSelected = field.value
                            ?.map((val: any) => val.toString())
                            .includes(option.value);

                          return (
                            <div
                              key={option.value}
                              role="option"
                              aria-selected={isSelected}
                              className={cn(
                                "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none",
                                "aero:hover:bg-neutral-100 aero:hover:text-foreground",
                                isSelected &&
                                  "aero:bg-brand-primary-50 aero:text-brand-primary-600"
                              )}
                              onClick={() => {
                                const newValues = isSelected
                                  ? field.value
                                      ?.map((val: any) => val.toString())
                                      .filter((f: any) => f !== option.value)
                                  : [...(field.value || []), option.value];
                                field.onChange(newValues);
                                if (onSelectItem) {
                                  onSelectItem(newValues);
                                }
                              }}
                            >
                              <span className="aero:truncate">
                                {option.label}
                              </span>
                              {isSelected && (
                                <Check className="aero:ml-auto aero:h-4 aero:w-4" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </FormControl>
            <div
              className={cn(preserveMessageSpace ? "aero:min-h-[18px]" : "")}
            >
              <FormMessage
                className={cn(
                  fieldState?.error
                    ? "aero:text-destructive aero:text-xs"
                    : preserveMessageSpace
                      ? "aero:invisible aero:text-xs"
                      : "aero:hidden",
                  messageClassName
                )}
              />
            </div>
          </FormItem>
        );
      }}
    />
  );
};
